import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { VersionedName } from '@/components/versionedName/versionedName';
import { EvaluationMetricRow, useEvaluationMetrics } from '@/hooks/queries/useMetrics';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { Evaluation } from '@/types/evals';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import styles from './metricsTable.module.scss';

const columnHelper = createColumnHelper<EvaluationMetricRow>();

type Props = {
  evaluation: Evaluation;
};

export function MetricsTable( { evaluation }: Props ) {
  const navigate = useNavigate();
  const [ useLocalZone ] = useLocalZonePreference();
  const { rows, isLoading, isError } = useEvaluationMetrics( evaluation.metric_providers );

  const columns = useMemo<ColumnDef<EvaluationMetricRow>[]>( () => [
    columnHelper.display( {
      id: 'metric',
      header: 'Metrics',
      cell: info => {
        const row = info.row.original;
        return (
          <VersionedName
            name={ row.name ?? row.metricProviderId }
            version={ row.version }
            isDeprecated={ row.isDeprecated }
            nameClassName={ styles.metric_link }
            data-testid={ `metric-row-name-${ row.metricProviderId }` }
          />
        );
      },
      meta: { classStr: buildColumnClassString( { width: 50, styles: ColumnModEnums.NO_OVERFLOW } ) }
    } ),
    columnHelper.display( {
      id: 'createdAt',
      header: 'Created',
      cell: info => {
        const { createdAt } = info.row.original;
        if ( !createdAt ) return <span className={ styles.muted }>—</span>;
        return (
          <TimeFormatter
            sourceType={ TimeSourceTypeEnum.ISO }
            value={ createdAt }
            opts={ { useLocalZone } }
            outputFormat={ OutputFormatEnum.STRING_SINGLE }
          />
        );
      },
      meta: { classStr: buildColumnClassString( { width: 50 } ) }
    } )
  ], [ useLocalZone ] );

  const table = useNativeTable<EvaluationMetricRow>( {
    data: rows,
    columns
  } );

  const handleRowClick = useCallback( ( row: EvaluationMetricRow ) => {
    if ( row.version == null ) return;
    const params = new URLSearchParams( {
      metric_provider_id: row.metricProviderId,
      version: String( row.version )
    } );
    navigate( `/evaluations/metric?${ params.toString() }` );
  }, [ navigate ] );

  const renderContent = () => {
    if ( isLoading ) return <Loader />;
    if ( isError ) return <div className={ styles.error } data-testid="metrics-error">Error loading metrics</div>;
    if ( rows.length === 0 ) {
      return (
        <div className={ styles.empty } data-testid="metrics-empty">
          No metrics are linked to this evaluation.
        </div>
      );
    }
    return (
      <NativeTable
        table={ table }
        tbodyTestId="table-body-metrics-tab"
        onRowClick={ handleRowClick }
      />
    );
  };

  return (
    <div className={ styles.layout } data-testid="tab-panel-metrics">
      { renderContent() }
    </div>
  );
}
